import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { useLoading } from "../../context/LoadingContext";
import { useAuth } from "../../context/AuthContext";

import { IoMdArrowBack } from "react-icons/io";
import { RiInformation2Fill } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { QRCodeCanvas } from "qrcode.react";
import ErrorBanner from "../ui/ErrorBanner";

import { fetchCryptoPendingList,
    updateTRCStatus
} from "../../utils/api";
import { assetPath } from "../../utils/assets";

const CryptoDepositAddress = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const { token } = useAuth();

    const [error, setError] = useState({
        message: "",
        shake: false
    });

    const [modal, setModal] = useState({
        show: false
    });
    const [data, setData] = useState(null);
    const [deposit, setDeposit] = useState({
        amount: "",
        countdown: null,
        expired_at: null,
    });

    const countdownCleanupRef = useRef(null);
    const lastExpiredAtRef = useRef(null);

    const startCountdown = useCallback((endTime) => {
        if (!endTime || isNaN(endTime)) return;

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const distance = endTime - now;

            if (distance <= 0) {
                clearInterval(interval);
                setDeposit(prev => ({
                    ...prev,
                    countdown: "EXPIRED"
                }));

                setTimeout(() => {
                    navigate("/crypto-deposit");
                }, 1000);

                return;
            }

            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            setDeposit(prev => ({
                ...prev,
                countdown: `${minutes}m ${seconds < 10 ? "0" : ""}${seconds}s`
            }));
        }, 1000);

        return () => clearInterval(interval);
    }, [navigate]);

    const clearError = useCallback(() => {
        setError(prev => ({ ...prev, message: "" }));
    }, []);

    const addressFromState = location.state?.address;
    const amountFromState = location.state?.amount;

    useEffect(() => {
        if (addressFromState) {
            setData(addressFromState);
            if (amountFromState) {
                setDeposit(prev => ({ ...prev, amount: amountFromState }));
            }
        }
    }, [addressFromState, amountFromState]);

    const applyPendingData = useCallback((pending) => {
        setData(pending?.receiver_address || 0);

        let endTime = new Date(pending.expired_at).getTime();
        endTime += 3600000;

        if (!pending.expired_at || isNaN(endTime)) {
            setTimeout(() => {
                navigate("/crypto-deposit");
            }, 1000);
            return;
        }

        setDeposit({
            amount: pending.unique_amount,
            countdown: null,
            expired_at: pending.expired_at,
        });

        if (lastExpiredAtRef.current !== pending.expired_at) {
            if (countdownCleanupRef.current) {
                countdownCleanupRef.current();
            }
            countdownCleanupRef.current = startCountdown(endTime);
            lastExpiredAtRef.current = pending.expired_at;
        }
    }, [navigate, startCountdown]);

    const loadData = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }

        try {
            const [cryptoPendingResponse] = await Promise.all([
                fetchCryptoPendingList()
            ]);

            if (cryptoPendingResponse.success && cryptoPendingResponse.data) {
                applyPendingData(cryptoPendingResponse.data);
            } else {
                setTimeout(() => {
                    navigate("/crypto-deposit");
                }, 1000);
            }
        } catch (error) {
            setError(prev => ({ ...prev, message: t("pleaseTryLater") }));
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [applyPendingData, navigate, setLoading, t]);

    useEffect(() => {
        loadData(true);

        return () => {
            if (countdownCleanupRef.current) {
                countdownCleanupRef.current();
            }
        };
    }, [loadData, token, addressFromState]);

    useEffect(() => {
        if (!token) return;

        const intervalId = setInterval(() => {
            loadData(false);
        }, 20000);

        return () => clearInterval(intervalId);
    }, [token, loadData]);

    const fallbackCopyTextToClipboard = useCallback((text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();

        try {
            document.execCommand("copy");
            alert(t("addressCopied"));
        } catch (err) {
            console.error("Fallback copy failed:", err);
        }

        document.body.removeChild(textArea);
    }, [t]);

    const handleCopyId = useCallback((id) => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard
                .writeText(id)
                .then(() => alert(t("addressCopied")))
                .catch((err) => {
                    console.error("Clipboard API failed, using fallback:", err);
                    fallbackCopyTextToClipboard(id);
                });
        } else {
            fallbackCopyTextToClipboard(id);
        }
    }, [t, fallbackCopyTextToClipboard]);

    const handleApi = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const response = await updateTRCStatus();
            
            if (response.success) {
                setTimeout(() => {
                    navigate("/crypto-deposit");
                }, 1000);
            } else {
                setError(prev => ({ ...prev, message: response.data || t("pleaseTryLater") }));
            }
        } catch (err) {
            setError(prev => ({ ...prev, message: t("failedToFetchLink") }));
        } finally {
            setLoading(false);
        }
    }, [t, clearError, navigate, setLoading]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/deposit")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <ErrorBanner message={error.message} />
            
            <div className="flex flex-col flex-1 overflow-y-auto py-3 px-4 items-center pt-[30px] pb-[100px]">
                <div className="relative w-full flex justify-center items-center mb-4">
                    <img
                        src={assetPath("/icons/andapay_logo.png")}
                        alt="logo"
                        className="w-[140px] h-[32px]"
                    />

                    <button
                        className="absolute top-0 right-0 cursor-pointer"
                        onClick={() => setModal(prev => ({ ...prev, show: true }))}
                    >
                        <IoClose className="size-lg text-color-blue" />
                    </button>
                </div>

                <div className="text-center mb-4">
                    {/* <p className="text-sm flex items-center justify-center gap-1">
                        <span className="font-bold text-white">
                            {t("payWith")}:
                        </span>
                        <img
                            src={assetPath("/icons/tether.png")}
                            alt="Tether"
                            className="size-md rounded-full"
                        />
                        <span className="text-white">
                            {t("USDT")}
                        </span>
                    </p> */}
                    <p className="text-sm">
                        <span className="font-bold text-white">
                            {t("USDT")}:
                        </span>{" "}
                        <span className="text-white">
                            {t("TRX Tron (TRC20)")}
                        </span>
                    </p>
                    <p className="text-md flex-center gap-1">
                        <span className="font-bold text-white">
                            {t("depositAmount")}:
                        </span>
                        <img
                            src={assetPath("/icons/tether.png")}
                            alt="Tether"
                            className="size-md rounded-full"
                        />
                        <span className="text-white">
                            {typeof deposit.amount === 'number' && deposit.amount !== null
                                ? deposit.amount.toFixed(2)
                                : deposit.amount} 
                            {t("USDT")}
                        </span>
                    </p>
                    {/* <p className="text-sm font-medium text-warning-bg-red">
                        {t("MinimumDeposit1oUSDT")}
                    </p> */}
                </div>

                <div className="bg-white mb-[21px] rounded">
                    <QRCodeCanvas
                        value={data}
                        size={168}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        includeMargin={true}
                    />
                </div>

                <div className="w-full max-w-md mb-2">
                    <p className="text-sm font-medium text-left text-color-white-opacity mb-1">
                        {t("depositAddress")}
                    </p>
                    <div className="flex items-center border border-white px-2 py-3 rounded-lg bg-transparent">
                        <span className="text-xs break-words text-[#009EF6] mr-[18px] flex-1 min-w-0">
                            {data}
                        </span>

                        <button
                            onClick={() => data && handleCopyId(data)}
                            className="bg-dropdown-bg text-white text-sm font-bold rounded-sm w-[86px] h-[36px]"
                        >
                            {t("copy")}
                        </button>
                    </div>
                </div>

                <div className="info-banner mb-5">
                    <RiInformation2Fill className="size-[30px] text-border-yellow" />
                    <div className="flex flex-col flex-grow">
                        <span className="text-xs text-white">
                            {t("youHaveToDepositAtLeast")}.{' '}
                            {t("minDeposit5USDT")}.
                        </span>
                    </div>
                </div>

                <div className="flex items-center w-full max-w-md">
                    <p className="text-sm font-medium text-color-white-opacity whitespace-nowrap">
                        {t("timeRemain")}
                    </p>
                    <div className="flex-1 h-px bg-color-white-opacity mx-2"></div>
                    <p className="font-bold text-white text-2xl">
                        {deposit.countdown}
                    </p>
                </div>
            </div>

            {modal.show && (
                <div className="modal-overlay">
                    
                    <div className="fixed z-200 inset-0 flex items-center bg-fourth bg-opacity-60 px-4 py-5">
                        <div className="bg-white rounded-sm p-4 text-center w-full">
                            <p className="text-sm font-bold mb-3">
                                {t("areYouSureToCancelDeposit")}?
                            </p>
                            <div className="flex justify-center gap-4">
                                <button
                                    className="bg-white border border-dropdown-bg border-2 text-dropdown-bg text-sm font-bold rounded-sm w-[86px] h-[36px]"
                                    onClick={() => setModal(prev => ({ ...prev, show: false }))}
                                >
                                    {t("no")}
                                </button>
                                <button
                                    className="bg-dropdown-bg text-white text-sm font-bold rounded-sm w-[86px] h-[36px]"
                                    onClick={() => {
                                        setModal(prev => ({ ...prev, show: false }))
                                        handleApi();
                                    }}
                                >
                                    {t("yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default CryptoDepositAddress;